import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ClipboardList, Save, Plus, Calendar as CalendarIcon, Pencil, Trash2, X, FileSpreadsheet, KeyRound, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  getNaves, getLotes, createRegistroDiario, getRegistrosDiarios,
  updateRegistroDiario, deleteRegistroDiario, getPosturaPorRegistro,
  verificarSuperCodigo,
} from '../lib/api';
import { formatDate, calcularSemanaEnFecha } from '../lib/dateUtils';
import { exportToExcel } from '../lib/exportUtils';
import { RegistrosFaltantesCard } from './produccion/RegistrosFaltantesCard';

// Helper para formato con separador de miles (es-ES usa puntos, en-US usa comas)
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtDec = (n, dec = 1) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const initialForm = {
  nave_id: '',
  fecha: new Date().toISOString().split('T')[0],
  grandes: '',
  medianos: '',
  pequenos: '',
  dobles: '',
  sucios: '',
  casquiados: '',
  mortalidad: '',
  observaciones_mortalidad: '',
  alimento_consumido: '',
};

export default function ProduccionDiaria() {
  const [naves, setNaves] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [form, setForm] = useState(initialForm);
  // Lote seleccionado en el formulario (para filtrar las naves disponibles).
  // No se envía al backend — solo es UI local.
  const [formLoteId, setFormLoteId] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filtroNave, setFiltroNave] = useState('all');
  const [filtroLote, setFiltroLote] = useState('all');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroSemana, setFiltroSemana] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [posturaMap, setPosturaMap] = useState({});
  // Trigger para forzar recarga de la card "Registros Faltantes" tras
  // crear / editar / eliminar un registro.
  const [faltantesRefreshKey, setFaltantesRefreshKey] = useState(0);

  // ---- Super Código (gate para editar/eliminar) ----
  // pendingAction: { type: 'edit' | 'delete', reg }
  const [pendingAction, setPendingAction] = useState(null);
  const [codigoInput, setCodigoInput] = useState('');
  const [verifying, setVerifying] = useState(false);

  // ---- Modal Conteo Posterior ----
  // Se abre cuando el backend detecta que hay un conteo físico igual o posterior
  // al registro que se está editando. Ofrece al usuario dos opciones:
  //  (a) corregir SOLO el número (sin tocar inventario) — recomendado
  //  (b) corregir Y ajustar inventario — forzado
  // En ambos casos requiere PIN de Super Usuario.
  const [conteoPosteriorDialog, setConteoPosteriorDialog] = useState({
    open: false,
    message: '',
    conteoFecha: '',
    registroFecha: '',
    pendingData: null, // payload del PUT a reintentar
    pin: '',
    ajustarInventario: false,
    submitting: false,
  });

  // Carga registros + postura. NO depende de `naves` para evitar bucles
  // infinitos cuando se actualiza el array de naves (refresh post-submit).
  const loadRegistros = useCallback(async () => {
    try {
      const params = {};
      if (filtroNave && filtroNave !== 'all') params.nave_id = filtroNave;
      if (filtroFechaDesde) params.fecha_desde = filtroFechaDesde;
      if (filtroFechaHasta) params.fecha_hasta = filtroFechaHasta;
      const res = await getRegistrosDiarios(params);
      setRegistros(res.data || []);
      // Cargar % postura en paralelo (ignora errores silenciosamente)
      try {
        const posturaRes = await getPosturaPorRegistro(params);
        setPosturaMap(posturaRes.data || {});
      } catch (postErr) {
        console.warn('No se pudo cargar % postura:', postErr?.message || postErr);
        setPosturaMap({});
      }
    } catch (err) {
      console.error('Error loading registros:', err);
    }
  }, [filtroNave, filtroFechaDesde, filtroFechaHasta]);

  // Carga inicial (una sola vez): naves + lotes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [navesRes, lotesRes] = await Promise.all([getNaves(), getLotes()]);
        if (cancelled) return;
        setNaves(navesRes.data || []);
        setLotes(lotesRes.data || []);
      } catch (err) {
        console.error('Error cargando datos:', err);
        toast.error('Error cargando datos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Recargar registros cada vez que cambien los filtros server-side
  useEffect(() => { loadRegistros(); }, [loadRegistros]);

  // Lookup maps: nave_id -> nave, lote_id -> lote (memoizados)
  const naveById = useMemo(() => {
    const m = new Map();
    for (const n of naves) m.set(n.id || n._id, n);
    return m;
  }, [naves]);

  const loteById = useMemo(() => {
    const m = new Map();
    for (const l of lotes) m.set(l.id || l._id, l);
    return m;
  }, [lotes]);

  // Helper: semana de edad de las gallinas en la fecha del registro
  const getSemanaRegistro = useCallback((reg) => {
    const nave = naveById.get(reg.nave_id);
    if (!nave) return null;
    const lote = loteById.get(nave.lote_id);
    if (!lote) return null;
    const fechaRef = lote.fecha_referencia_edad || lote.fecha_ingreso;
    if (!fechaRef) return null;
    return calcularSemanaEnFecha(reg.fecha, fechaRef);
  }, [naveById, loteById]);

  // Filtrado en frontend (derivado, no estado).
  const registrosFiltrados = useMemo(() => {
    let data = registros || [];
    // Filtro por lote (solo si no hay nave específica)
    if (filtroLote && filtroLote !== 'all' && (!filtroNave || filtroNave === 'all')) {
      const navesDelLote = new Set(
        naves
          .filter((n) => (n.lote_id || '') === filtroLote)
          .map((n) => n.id || n._id),
      );
      data = data.filter((r) => navesDelLote.has(r.nave_id));
    }
    // Filtro por semana de edad al momento del registro
    const semanaNum = parseInt(filtroSemana, 10);
    if (Number.isInteger(semanaNum) && semanaNum >= 0 && filtroSemana !== '') {
      data = data.filter((r) => getSemanaRegistro(r) === semanaNum);
    }
    return data;
  }, [registros, naves, filtroLote, filtroNave, filtroSemana, getSemanaRegistro]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Enter avanza al siguiente <input> del formulario.
  // En el último input ejecuta "Guardar".
  const handleFormEnter = (e) => {
    if (e.key !== 'Enter') return;
    const target = e.target;
    if (!target || target.tagName !== 'INPUT') return;
    // Permitir Enter normal en checkboxes/radio
    const t = (target.type || '').toLowerCase();
    if (t === 'checkbox' || t === 'radio' || t === 'submit' || t === 'button') return;
    e.preventDefault();
    const root = e.currentTarget;
    const inputs = Array.from(
      root.querySelectorAll('input:not([disabled]):not([type="hidden"])'),
    ).filter((el) => el.offsetParent !== null); // visibles
    const idx = inputs.indexOf(target);
    if (idx === -1) return;
    if (idx < inputs.length - 1) {
      const next = inputs[idx + 1];
      next.focus();
      try {
        if (typeof next.select === 'function') next.select();
      } catch (_) { /* noop */ }
    } else if (!saving) {
      // Último input → Guardar
      handleSubmit(false);
    }
  };

  const handleSubmit = async (andNew = false) => {
    if (!form.nave_id || !form.fecha) {
      toast.error('Seleccione una nave y fecha');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        grandes: parseInt(form.grandes) || 0,
        medianos: parseInt(form.medianos) || 0,
        pequenos: parseInt(form.pequenos) || 0,
        dobles: parseInt(form.dobles) || 0,
        sucios: parseInt(form.sucios) || 0,
        casquiados: parseInt(form.casquiados) || 0,
        mortalidad: parseInt(form.mortalidad) || 0,
        alimento_consumido: parseFloat(form.alimento_consumido) || 0,
        observaciones_mortalidad: (form.observaciones_mortalidad || '').trim() || null,
      };

      if (editingId) {
        try {
          await updateRegistroDiario(editingId, data);
          toast.success('Registro actualizado e inventario ajustado');
        } catch (err) {
          // Conteo físico posterior detectado por el backend (HTTP 409)
          const status = err.response?.status;
          const detail = err.response?.data?.detail;
          if (status === 409 && detail && typeof detail === 'object' && detail.error === 'conteo_posterior_existente') {
            // Abrir el modal profesional (Shadcn) en vez de window.confirm
            setConteoPosteriorDialog({
              open: true,
              message: detail.message || '',
              conteoFecha: detail.conteo_fecha || '',
              registroFecha: detail.registro_fecha || '',
              pendingData: data,
              pin: '',
              ajustarInventario: false,
              submitting: false,
            });
            setSaving(false);
            return;
          }
          throw err; // Reenviar para el manejo genérico de errores abajo
        }
        setEditingId(null);
        setForm(initialForm);
        setFormLoteId('');
      } else {
        await createRegistroDiario(data);
        toast.success('Registro guardado exitosamente');

        if (andNew) {
          // Mantener fecha y lote para registrar la siguiente nave del mismo lote
          setForm({
            ...initialForm,
            fecha: form.fecha,
            nave_id: '',
          });
        } else {
          setForm(initialForm);
          setFormLoteId('');
        }
      }
      loadRegistros();
      // Refresh naves to update gallinas_actuales after mortalidad changes
      const navesRes = await getNaves();
      setNaves(navesRes.data);
      // Recargar la card de registros faltantes
      setFaltantesRefreshKey((k) => k + 1);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Error guardando registro');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectFaltante = ({ nave_id, nave_nombre, fecha }) => {
    // Cancelar edición si la había
    if (editingId) setEditingId(null);
    // Sincronizar el lote del formulario con el de la nave seleccionada
    const naveObj = naves.find((n) => (n.id || n._id) === nave_id);
    if (naveObj?.lote_id) setFormLoteId(naveObj.lote_id);
    setForm({ ...initialForm, nave_id, fecha });
    // Scroll al formulario
    const formEl = document.querySelector('[data-testid="form-produccion-diaria"]');
    if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    toast.info(`Pre-cargado: ${nave_nombre} · ${fecha}`);
  };


  const handleEdit = (reg) => {
    setEditingId(reg.id || reg._id);
    // Sincronizar el lote del formulario con el del registro editado
    const naveDelReg = naves.find((n) => (n.id || n._id) === reg.nave_id);
    setFormLoteId(naveDelReg?.lote_id || '');
    setForm({
      nave_id: reg.nave_id || '',
      fecha: reg.fecha?.split('T')[0] || new Date().toISOString().split('T')[0],
      grandes: String(reg.grandes ?? ''),
      medianos: String(reg.medianos ?? ''),
      pequenos: String(reg.pequenos ?? ''),
      dobles: String(reg.dobles ?? ''),
      sucios: String(reg.sucios ?? ''),
      casquiados: String(reg.casquiados ?? ''),
      mortalidad: String(reg.mortalidad ?? ''),
      observaciones_mortalidad: reg.observaciones_mortalidad || '',
      alimento_consumido: String(reg.alimento_consumido ?? ''),
    });
    // Scroll up to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Solicitar el código super antes de editar o eliminar
  const requestEdit = (reg) => {
    setPendingAction({ type: 'edit', reg });
    setCodigoInput('');
  };

  const requestDelete = (reg) => {
    setPendingAction({ type: 'delete', reg });
    setCodigoInput('');
  };

  const handleVerifyCodigo = async () => {
    if (!pendingAction) return;
    const codigo = codigoInput.trim();
    if (codigo.length < 4) {
      toast.error('Ingrese el código de Super Usuario');
      return;
    }
    setVerifying(true);
    try {
      await verificarSuperCodigo(codigo);
      // Código válido — ejecutar la acción
      const { type, reg } = pendingAction;
      setPendingAction(null);
      setCodigoInput('');
      if (type === 'edit') {
        handleEdit(reg);
      } else if (type === 'delete') {
        setDeletingId(reg.id || reg._id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código de Super Usuario incorrecto');
    } finally {
      setVerifying(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(initialForm);
    setFormLoteId('');
  };

  const handleConfirmConteoPosterior = async () => {
    const { pendingData, pin, ajustarInventario } = conteoPosteriorDialog;
    const codigo = (pin || '').trim();
    if (codigo.length < 4) {
      toast.error('Ingrese el código de Super Usuario');
      return;
    }
    if (!pendingData || !editingId) {
      toast.error('No hay datos pendientes para actualizar');
      setConteoPosteriorDialog((p) => ({ ...p, open: false }));
      return;
    }
    setConteoPosteriorDialog((p) => ({ ...p, submitting: true }));
    try {
      await updateRegistroDiario(editingId, pendingData, {
        confirmar_con_conteo_posterior: true,
        ajustar_inventario: ajustarInventario,
        super_codigo: codigo,
      });
      toast.success(
        ajustarInventario
          ? 'Registro actualizado — inventario ajustado (verifique conteos)'
          : 'Registro corregido sin tocar inventario',
      );
      setConteoPosteriorDialog({ open: false, message: '', conteoFecha: '', registroFecha: '', pendingData: null, pin: '', ajustarInventario: false, submitting: false });
      setEditingId(null);
      setForm(initialForm);
      setFormLoteId('');
      await loadRegistros();
      const navesRes = await getNaves();
      setNaves(navesRes.data);
      setFaltantesRefreshKey((k) => k + 1);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Error actualizando registro');
      toast.error(msg);
      setConteoPosteriorDialog((p) => ({ ...p, submitting: false }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    setDeleting(true);
    try {
      await deleteRegistroDiario(deletingId);
      toast.success('Registro eliminado e inventario revertido');
      setDeletingId(null);
      loadRegistros();
      const navesRes = await getNaves();
      setNaves(navesRes.data);
      setFaltantesRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando registro');
    } finally {
      setDeleting(false);
    }
  };

  const exportarRegistrosExcel = () => {
    const ok = exportToExcel(
      registrosFiltrados,
      [
        { label: 'Fecha', accessor: (r) => formatDate(r.fecha) },
        { label: 'Nave', accessor: (r) => {
          const n = naves.find(x => (x.id || x._id) === r.nave_id);
          return r.nave_nombre || n?.nombre_nave || 'N/A';
        } },
        { label: 'Semana', accessor: (r) => {
          const s = getSemanaRegistro(r);
          return s === null ? '' : s;
        } },
        { label: 'Grandes', key: 'grandes' },
        { label: 'Medianos', key: 'medianos' },
        { label: 'Pequeños', key: 'pequenos' },
        { label: 'Dobles', key: 'dobles' },
        { label: 'Sucios', key: 'sucios' },
        { label: 'Casquiados', key: 'casquiados' },
        { label: 'Total', accessor: (r) => (r.grandes||0)+(r.medianos||0)+(r.pequenos||0)+(r.dobles||0)+(r.sucios||0)+(r.casquiados||0) },
        { label: 'Mortalidad', key: 'mortalidad' },
        { label: 'Alimento (qq)', key: 'alimento_consumido' },
        { label: 'Observaciones', key: 'observaciones' },
      ],
      'produccion_diaria',
      'Producción'
    );
    if (!ok) toast.error('No hay registros para exportar');
    else toast.success('Excel descargado');
  };

  const totalHuevos = (
    (parseInt(form.grandes) || 0) +
    (parseInt(form.medianos) || 0) +
    (parseInt(form.pequenos) || 0) +
    (parseInt(form.dobles) || 0) +
    (parseInt(form.sucios) || 0) +
    (parseInt(form.casquiados) || 0)
  );

  const selectedNave = naves.find(n => (n.id || n._id) === form.nave_id);

  // Naves disponibles para el lote + fecha actual del formulario.
  // Excluye las naves que ya tienen un registro para esa fecha (excepto si
  // estamos editando ese mismo registro).
  const navesYaRegistradasEnFecha = new Set(
    (registros || [])
      .filter((r) => (r.fecha || '').slice(0, 10) === form.fecha)
      .filter((r) => !(editingId && (r.id || r._id) === editingId))
      .map((r) => r.nave_id),
  );

  // Helper: extrae el número de un nombre tipo "Nave 1", "Nave 12", etc.
  // Si no encuentra número, retorna Infinity para que vaya al final.
  const getNaveNumero = (nombre) => {
    if (!nombre) return Infinity;
    const m = String(nombre).match(/(\d+)/);
    return m ? parseInt(m[1], 10) : Infinity;
  };

  const navesParaForm = naves
    .filter((n) => !formLoteId || n.lote_id === formLoteId)
    .filter((n) => !navesYaRegistradasEnFecha.has(n.id || n._id))
    .slice()
    .sort((a, b) => {
      const na = getNaveNumero(a.nombre_nave);
      const nb = getNaveNumero(b.nombre_nave);
      if (na !== nb) return na - nb;
      // Fallback alfabético si no hay números
      return String(a.nombre_nave || '').localeCompare(String(b.nombre_nave || ''), 'es');
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alerta de registros pendientes / atrasados */}
      <RegistrosFaltantesCard
        onSelectFaltante={handleSelectFaltante}
        refreshKey={faltantesRefreshKey}
      />

      {/* Registration Form */}
      <Card data-testid="form-produccion-diaria">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <ClipboardList className="w-5 h-5" />
              {editingId ? 'Editar Registro de Producción' : 'Registrar Producción Diaria'}
            </CardTitle>
            {editingId && (
              <Button variant="ghost" size="sm" onClick={handleCancelEdit} data-testid="produccion-cancelar-edit">
                <X className="w-4 h-4 mr-1" /> Cancelar edición
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6" onKeyDown={handleFormEnter}>
            {/* Base Data */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="prod-fecha">Fecha *</Label>
                <Input
                  id="prod-fecha"
                  type="date"
                  data-testid="produccion-fecha"
                  value={form.fecha}
                  onChange={(e) => {
                    const newFecha = e.target.value;
                    handleChange('fecha', newFecha);
                    // Si la nave actualmente seleccionada ya tiene registro en la nueva fecha, limpiarla
                    if (form.nave_id && newFecha) {
                      const conflicto = (registros || []).some((r) => (
                        (r.fecha || '').slice(0, 10) === newFecha
                        && r.nave_id === form.nave_id
                        && !(editingId && (r.id || r._id) === editingId)
                      ));
                      if (conflicto) {
                        setForm((f) => ({ ...f, nave_id: '' }));
                      }
                    }
                  }}
                  className="h-11 mt-1.5"
                />
              </div>
              <div>
                <Label>Lote *</Label>
                <Select
                  value={formLoteId}
                  onValueChange={(v) => {
                    setFormLoteId(v);
                    // Si la nave actual no pertenece al nuevo lote, limpiarla
                    if (form.nave_id) {
                      const naveActual = naves.find((n) => (n.id || n._id) === form.nave_id);
                      if (!naveActual || naveActual.lote_id !== v) {
                        setForm((f) => ({ ...f, nave_id: '' }));
                      }
                    }
                  }}
                  data-testid="produccion-lote-select"
                  disabled={!!editingId}
                >
                  <SelectTrigger className="h-11 mt-1.5">
                    <SelectValue placeholder="Seleccione un lote" />
                  </SelectTrigger>
                  <SelectContent>
                    {lotes.map((l) => (
                      <SelectItem key={l.id || l._id} value={l.id || l._id}>
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{l.numero_lote}</span>
                          {l.fase && (
                            <span className="text-muted-foreground text-xs">— {l.fase}</span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nave *</Label>
                <Select
                  value={form.nave_id}
                  onValueChange={(v) => handleChange('nave_id', v)}
                  data-testid="produccion-nave-select"
                  disabled={!formLoteId && !editingId}
                >
                  <SelectTrigger className="h-11 mt-1.5">
                    <SelectValue placeholder={
                      !formLoteId && !editingId
                        ? 'Seleccione un lote primero'
                        : navesParaForm.length === 0
                          ? 'Sin naves disponibles'
                          : 'Seleccione una nave'
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {/* En modo edición, mostrar también la nave actual aunque no esté en navesParaForm */}
                    {editingId && form.nave_id && !navesParaForm.some((n) => (n.id || n._id) === form.nave_id) && (
                      (() => {
                        const editingNave = naves.find((n) => (n.id || n._id) === form.nave_id);
                        return editingNave ? (
                          <SelectItem key={editingNave.id || editingNave._id} value={editingNave.id || editingNave._id}>
                            <span className="flex items-center gap-2">
                              <span className="font-medium">{editingNave.nombre_nave}</span>
                              <span className="text-muted-foreground text-xs">— {editingNave.lote_nombre || 'Sin lote'}</span>
                            </span>
                          </SelectItem>
                        ) : null;
                      })()
                    )}
                    {navesParaForm.map((n) => (
                      <SelectItem key={n.id || n._id} value={n.id || n._id}>
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{n.nombre_nave}</span>
                          {typeof n.semanas_edad === 'number' && (
                            <Badge variant="outline" className="text-[10px] tabular-nums px-1.5 py-0 ml-1">
                              {n.semanas_edad} sem.
                            </Badge>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                    {navesParaForm.length === 0 && !editingId && formLoteId && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        Todas las naves de este lote ya tienen registro para {form.fecha}.
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {selectedNave && (
                <>
                  <div className="flex items-end">
                    <div className="bg-accent rounded-lg px-4 py-2.5 w-full" data-testid="produccion-gallinas-info">
                      <p className="text-xs text-muted-foreground">Gallinas actuales</p>
                      <p className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                        {fmt(selectedNave.gallinas_actuales)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-end">
                    <div className="bg-primary/10 border border-primary/20 rounded-lg px-4 py-2.5 w-full" data-testid="produccion-semanas-info">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" /> Edad de las gallinas
                      </p>
                      <p className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                        {selectedNave.semanas_edad ?? 0} <span className="text-sm font-medium text-muted-foreground">semanas</span>
                      </p>
                      {selectedNave.lote_nombre && (
                        <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                          Lote: {selectedNave.lote_nombre}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <Separator />

            {/* Mortality + Feed */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 tracking-wide">MORTALIDAD Y ALIMENTO</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="alimento">Alimento Consumido</Label>
                  <Input
                    id="alimento"
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0"
                    placeholder="0.0"
                    value={form.alimento_consumido}
                    onChange={(e) => handleChange('alimento_consumido', e.target.value)}
                    className="h-11 mt-1.5"
                  />
                  <p className="text-xs text-muted-foreground mt-0.5">qq (quintales)</p>
                </div>
                <div>
                  <Label htmlFor="mortalidad">Mortalidad</Label>
                  <Input
                    id="mortalidad"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    placeholder="0"
                    value={form.mortalidad}
                    onChange={(e) => handleChange('mortalidad', e.target.value)}
                    className="h-11 mt-1.5"
                    data-testid="input-mortalidad"
                  />
                  <p className="text-xs text-muted-foreground mt-0.5">aves</p>
                </div>
                <div>
                  <Label htmlFor="observaciones_mortalidad">Observación (causa)</Label>
                  <Input
                    id="observaciones_mortalidad"
                    type="text"
                    placeholder="Ej: golpe de calor, enfermedad, sofocación..."
                    value={form.observaciones_mortalidad}
                    onChange={(e) => handleChange('observaciones_mortalidad', e.target.value)}
                    className="h-11 mt-1.5"
                    disabled={!Number(form.mortalidad || 0)}
                    data-testid="input-observaciones-mortalidad"
                  />
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {Number(form.mortalidad || 0) > 0 ? 'Motivo de la mortalidad' : 'Se habilita cuando hay mortalidad'}
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Eggs Section */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 tracking-wide">RECOLECCIÓN DE HUEVOS</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { key: 'grandes', label: 'Grandes' },
                  { key: 'medianos', label: 'Medianos' },
                  { key: 'pequenos', label: 'Pequeños' },
                  { key: 'dobles', label: 'Dobles' },
                  { key: 'sucios', label: 'Sucios' },
                  { key: 'casquiados', label: 'Casquiados' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <Label htmlFor={key} className="text-xs">{label}</Label>
                    <Input
                      id={key}
                      type="number"
                      inputMode="numeric"
                      min="0"
                      placeholder="0"
                      value={form[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                      className="h-11 mt-1 tabular-nums"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">unid.</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 bg-accent rounded-lg px-4 py-2 inline-flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Total huevos:</span>
                <span className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
                  {fmt(totalHuevos)}
                </span>
              </div>
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => handleSubmit(false)} disabled={saving} data-testid="produccion-guardar">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : (editingId ? 'Actualizar Registro' : 'Guardar')}
              </Button>
              {!editingId && (
                <Button variant="outline" onClick={() => handleSubmit(true)} disabled={saving} data-testid="produccion-guardar-nuevo">
                  <Plus className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar y Nuevo'}
                </Button>
              )}
              {editingId && (
                <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
                  Cancelar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Records */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Registros Recientes</CardTitle>
          <div className="flex flex-wrap gap-2 items-center">
            <Select
              value={filtroLote}
              onValueChange={(v) => {
                setFiltroLote(v);
                // Si la nave seleccionada no pertenece al nuevo lote, reiniciarla
                if (v !== 'all' && filtroNave !== 'all') {
                  const nave = naves.find((n) => (n.id || n._id) === filtroNave);
                  if (!nave || nave.lote_id !== v) setFiltroNave('all');
                }
              }}
            >
              <SelectTrigger className="w-[180px] h-9" data-testid="filtro-lote">
                <SelectValue placeholder="Todos los lotes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los lotes</SelectItem>
                {lotes.map((l) => (
                  <SelectItem key={l.id || l._id} value={l.id || l._id}>
                    {l.numero_lote}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroNave} onValueChange={setFiltroNave}>
              <SelectTrigger className="w-[180px] h-9">
                <SelectValue placeholder="Todas las naves" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las naves</SelectItem>
                {naves
                  .filter((n) => filtroLote === 'all' || n.lote_id === filtroLote)
                  .slice()
                  .sort((a, b) => {
                    const na = getNaveNumero(a.nombre_nave);
                    const nb = getNaveNumero(b.nombre_nave);
                    if (na !== nb) return na - nb;
                    return String(a.nombre_nave || '').localeCompare(String(b.nombre_nave || ''), 'es');
                  })
                  .map((n) => (
                    <SelectItem key={n.id || n._id} value={n.id || n._id}>{n.nombre_nave}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              placeholder="Desde"
              value={filtroFechaDesde}
              onChange={(e) => setFiltroFechaDesde(e.target.value)}
              className="w-[140px] h-9"
            />
            <Input
              type="date"
              placeholder="Hasta"
              value={filtroFechaHasta}
              onChange={(e) => setFiltroFechaHasta(e.target.value)}
              className="w-[140px] h-9"
            />
            <Input
              type="number"
              inputMode="numeric"
              min="0"
              placeholder="Semana #"
              value={filtroSemana}
              onChange={(e) => setFiltroSemana(e.target.value.replace(/[^0-9]/g, ''))}
              className="w-[110px] h-9 tabular-nums"
              data-testid="filtro-semana"
              title="Filtrar por la semana de edad de las gallinas en la fecha del registro"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFiltroLote('all'); setFiltroNave('all'); setFiltroFechaDesde(''); setFiltroFechaHasta(''); setFiltroSemana(''); }}
              data-testid="filtro-limpiar"
            >
              Limpiar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportarRegistrosExcel}
              disabled={registrosFiltrados.length === 0}
              data-testid="btn-export-registros-excel"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {registrosFiltrados.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No hay registros de producción</p>
            </div>
          ) : (
            <Table
              wrapperClassName="max-h-[calc(100vh-280px)] rounded-md border border-border/40"
              data-testid="tabla-produccion-scroll"
            >
                <TableHeader className="sticky top-0 z-30 bg-card shadow-[0_2px_4px_-1px_rgba(0,0,0,0.08)] [&_tr]:border-b-0 [&_th]:border-b [&_th]:border-border">

                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Lote</TableHead>
                    <TableHead>Nave</TableHead>
                    <TableHead className="text-right">Sem.</TableHead>
                    <TableHead className="text-right">Grandes</TableHead>
                    <TableHead className="text-right">Medianos</TableHead>
                    <TableHead className="text-right">Pequeños</TableHead>
                    <TableHead className="text-right">Dobles</TableHead>
                    <TableHead className="text-right">Sucios</TableHead>
                    <TableHead className="text-right">Casq.</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">% Postura</TableHead>
                    <TableHead className="text-right">Mort.</TableHead>
                    <TableHead className="text-right">Alimento</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registrosFiltrados.map((reg, idx) => {
                    const total = (reg.grandes || 0) + (reg.medianos || 0) + (reg.pequenos || 0) + (reg.dobles || 0) + (reg.sucios || 0) + (reg.casquiados || 0);
                    const naveObj = naves.find(n => (n.id || n._id) === reg.nave_id);
                    const naveLabel = reg.nave_nombre || naveObj?.nombre_nave || 'N/A';
                    const loteLabel = reg.lote_nombre || naveObj?.lote_nombre || '—';
                    const regId = reg.id || reg._id;
                    const isEditing = editingId === regId;
                    // Separador visual al cambiar de fecha (igual que en
                    // Análisis de Producción) — facilita ver bloques por día.
                    const fechaActual = String(reg.fecha || '').slice(0, 10);
                    const fechaPrev = idx > 0 ? String(registrosFiltrados[idx - 1].fecha || '').slice(0, 10) : null;
                    const cambioFecha = idx === 0 || fechaActual !== fechaPrev;
                    return (
                      <React.Fragment key={regId}>
                        {cambioFecha && (
                          <TableRow
                            className="bg-primary/10 hover:bg-primary/15 border-t-2 border-primary/40"
                            data-testid={`divisor-fecha-${fechaActual}`}
                          >
                            <TableCell colSpan={15} className="py-1.5">
                              <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                                <CalendarIcon className="w-3.5 h-3.5" />
                                <span style={{ fontFamily: 'Space Grotesk' }}>
                                  {formatDate(reg.fecha)}
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow
                          className={`hover:bg-accent/60 transition-colors duration-150 ${isEditing ? 'bg-accent/40' : ''}`}
                        >
                        <TableCell>{formatDate(reg.fecha)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs font-medium">
                            {loteLabel}
                          </Badge>
                        </TableCell>
                        <TableCell>{naveLabel}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {(() => {
                            const sem = getSemanaRegistro(reg);
                            return sem === null ? <span className="text-muted-foreground">—</span> : (
                              <Badge variant="outline" className="text-[10px] tabular-nums px-1.5 py-0">
                                {sem}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(reg.grandes)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(reg.medianos)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(reg.pequenos)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(reg.dobles)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(reg.sucios)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(reg.casquiados)}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{fmt(total)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {(() => {
                            const p = posturaMap[regId];
                            const pct = p?.postura_pct;
                            if (pct === undefined || pct === null) return <span className="text-muted-foreground">—</span>;
                            const tooltip = p?.gallinas_activas ? `${fmt(p.gallinas_activas)} gallinas` : '';
                            // Postura óptima ≥ 90%: dorado
                            if (pct >= 90) {
                              return (
                                <Badge
                                  className="text-xs tabular-nums bg-amber-400 text-amber-950 border border-amber-600 hover:bg-amber-400 font-bold shadow-sm"
                                  title={`Óptimo · ${tooltip}`}
                                >
                                  ★ {pct.toFixed(1)}%
                                </Badge>
                              );
                            }
                            const variant = pct >= 80 ? 'default' : (pct >= 60 ? 'secondary' : 'destructive');
                            return (
                              <Badge variant={variant} className="text-xs tabular-nums" title={tooltip}>
                                {pct.toFixed(1)}%
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {reg.mortalidad > 0 ? (
                            <div className="flex items-center justify-end gap-1">
                              <Badge variant="destructive" className="text-xs">{fmt(reg.mortalidad)}</Badge>
                              {reg.observaciones_mortalidad && (
                                <span
                                  className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold cursor-help"
                                  title={`Causa: ${reg.observaciones_mortalidad}`}
                                >
                                  ?
                                </span>
                              )}
                            </div>
                          ) : '0'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtDec(reg.alimento_consumido, 1)} qq</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => requestEdit(reg)}
                            data-testid={`btn-edit-registro-${regId}`}
                            title="Editar (requiere código)"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => requestDelete(reg)}
                            data-testid={`btn-delete-registro-${regId}`}
                            title="Eliminar (requiere código)"
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
          )}
        </CardContent>
      </Card>
      {/* Confirmar eliminación */}
      <AlertDialog open={!!deletingId} onOpenChange={(o) => { if (!o) setDeletingId(null); }}>
        <AlertDialogContent data-testid="confirmar-eliminar-registro">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción restará los huevos del inventario y devolverá la mortalidad a la nave. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ==== Modal: Código de Super Usuario (gate para editar/eliminar) ==== */}
      <Dialog
        open={!!pendingAction}
        onOpenChange={(o) => { if (!o) { setPendingAction(null); setCodigoInput(''); } }}
      >
        <DialogContent className="sm:max-w-md" data-testid="modal-super-codigo">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <KeyRound className="w-5 h-5" />
              Código de Super Usuario requerido
            </DialogTitle>
            <DialogDescription className="pt-1">
              {pendingAction?.type === 'delete'
                ? 'Para eliminar este registro, ingrese el código de Super Usuario.'
                : 'Para editar este registro, ingrese el código de Super Usuario.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-md p-2.5">
              <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-amber-900 dark:text-amber-200">
                Esta acción puede afectar inventario y conteos. Solicite el código al Super Usuario.
              </p>
            </div>
            <div>
              <Label htmlFor="super-codigo-input">Código</Label>
              <Input
                id="super-codigo-input"
                type="password"
                value={codigoInput}
                onChange={(e) => setCodigoInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !verifying) handleVerifyCodigo(); }}
                placeholder="Ingrese el código"
                className="h-11 mt-1.5 font-mono"
                autoFocus
                data-testid="input-super-codigo"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setPendingAction(null); setCodigoInput(''); }}
              disabled={verifying}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleVerifyCodigo}
              disabled={verifying || codigoInput.trim().length < 4}
              data-testid="btn-verificar-super-codigo"
            >
              {verifying ? 'Verificando...' : (pendingAction?.type === 'delete' ? 'Confirmar y eliminar' : 'Confirmar y editar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==== Modal: Conteo Físico Posterior detectado ==== */}
      <Dialog
        open={conteoPosteriorDialog.open}
        onOpenChange={(o) => {
          if (!o && !conteoPosteriorDialog.submitting) {
            setConteoPosteriorDialog((p) => ({ ...p, open: false, pin: '', pendingData: null, ajustarInventario: false }));
          }
        }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="modal-conteo-posterior">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400" style={{ fontFamily: 'Space Grotesk' }}>
              <ShieldAlert className="w-5 h-5" />
              Existe un conteo físico posterior
            </DialogTitle>
            <DialogDescription className="pt-1">
              Estás editando un registro cuya fecha ya está cubierta por un conteo físico. Elige cómo proceder.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Info del conflicto */}
            <div className="grid grid-cols-2 gap-2 text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-md p-3">
              <div>
                <div className="text-amber-900/70 dark:text-amber-300/70 font-medium">Registro editado</div>
                <div className="font-semibold text-amber-900 dark:text-amber-100">{conteoPosteriorDialog.registroFecha || '—'}</div>
              </div>
              <div>
                <div className="text-amber-900/70 dark:text-amber-300/70 font-medium">Conteo físico</div>
                <div className="font-semibold text-amber-900 dark:text-amber-100">{conteoPosteriorDialog.conteoFecha || '—'}</div>
              </div>
            </div>

            {/* Opciones */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">¿Qué querés hacer?</Label>
              <div className="space-y-2">
                <label
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    !conteoPosteriorDialog.ajustarInventario
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="conteo-posterior-modo"
                    checked={!conteoPosteriorDialog.ajustarInventario}
                    onChange={() => setConteoPosteriorDialog((p) => ({ ...p, ajustarInventario: false }))}
                    className="mt-1 accent-primary"
                    data-testid="radio-solo-corregir"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Corregir SOLO el registro (recomendado)</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Actualiza el número histórico sin tocar el stock actual. Ideal si el conteo físico ya reajustó el inventario.
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    conteoPosteriorDialog.ajustarInventario
                      ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="conteo-posterior-modo"
                    checked={conteoPosteriorDialog.ajustarInventario}
                    onChange={() => setConteoPosteriorDialog((p) => ({ ...p, ajustarInventario: true }))}
                    className="mt-1 accent-red-500"
                    data-testid="radio-corregir-ajustar"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-red-700 dark:text-red-400">Corregir Y ajustar inventario</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Suma/resta la diferencia al stock. Puede descuadrar el inventario si el conteo físico ya lo corrigió.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* PIN */}
            <div>
              <Label htmlFor="conteo-posterior-pin" className="flex items-center gap-1.5 text-sm">
                <KeyRound className="w-4 h-4" />
                Código de Super Usuario
              </Label>
              <Input
                id="conteo-posterior-pin"
                type="password"
                value={conteoPosteriorDialog.pin}
                onChange={(e) => setConteoPosteriorDialog((p) => ({ ...p, pin: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !conteoPosteriorDialog.submitting && conteoPosteriorDialog.pin.trim().length >= 4) {
                    handleConfirmConteoPosterior();
                  }
                }}
                placeholder="Ingrese el código"
                className="h-10 mt-1.5 font-mono"
                autoFocus
                data-testid="input-pin-conteo-posterior"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConteoPosteriorDialog((p) => ({ ...p, open: false, pin: '', pendingData: null, ajustarInventario: false }))}
              disabled={conteoPosteriorDialog.submitting}
              data-testid="btn-cancelar-conteo-posterior"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmConteoPosterior}
              disabled={conteoPosteriorDialog.submitting || conteoPosteriorDialog.pin.trim().length < 4}
              variant={conteoPosteriorDialog.ajustarInventario ? 'destructive' : 'default'}
              data-testid="btn-confirmar-conteo-posterior"
            >
              {conteoPosteriorDialog.submitting ? 'Guardando…' : (
                conteoPosteriorDialog.ajustarInventario ? 'Editar y ajustar inventario' : 'Editar solo el registro'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
